import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WaitlistSignupData {
  email: string;
  first_name?: string;
  last_name?: string;
  zip_code: string;
  experience_level: string;
  vehicle_type?: string;
  features_of_interest: string[];
  referral_source?: string;
  referred_by_code?: string;
  adventure_style?: string;
  age_range?: string;
}

export interface WaitlistSignupResult {
  waitlist_position: number;
  referral_code: string;
}

export const useWaitlist = () => {
  const queryClient = useQueryClient();

  // Get waitlist count (only returns if >= threshold)
  const { data: waitlistCount } = useQuery({
    queryKey: ["waitlist-count"],
    queryFn: async () => {
      // Use service role via edge function to get count since we don't have SELECT policy
      const { count, error } = await supabase
        .from("waitlist_signups")
        .select("*", { count: "exact", head: true });

      if (error) {
        console.log("Count query error (expected without SELECT policy):", error);
        return null;
      }

      return count;
    },
    staleTime: 60000, // Cache for 1 minute
  });

  // Submit to waitlist
  const submitMutation = useMutation({
    mutationFn: async (data: WaitlistSignupData): Promise<WaitlistSignupResult> => {
      // Insert into waitlist_signups table
      // Note: referral_code and waitlist_position are auto-generated by trigger
      const insertPayload = {
        email: data.email,
        first_name: data.first_name || null,
        last_name: data.last_name || null,
        zip_code: data.zip_code,
        experience_level: data.experience_level,
        vehicle_type: data.vehicle_type || null,
        features_of_interest: data.features_of_interest,
        referral_source: data.referral_source || null,
        referred_by_code: data.referred_by_code || null,
        adventure_style: data.adventure_style || null,
        age_range: data.age_range || null,
        // These will be overwritten by the trigger, but TypeScript needs them
        referral_code: "TEMP",
        waitlist_position: 0,
      };
      
      const { data: insertedData, error: insertError } = await supabase
        .from("waitlist_signups")
        .insert(insertPayload)
        .select("waitlist_position, referral_code")
        .single();

      if (insertError) {
        console.error("Waitlist insert error:", insertError);
        if (insertError.code === "23505") {
          throw new Error("This email is already on the waitlist!");
        }
        throw new Error(insertError.message);
      }

      // Send confirmation email via edge function
      const referralLink = `${window.location.origin}?ref=${insertedData.referral_code}`;
      
      try {
        const { error: emailError } = await supabase.functions.invoke(
          "send-waitlist-confirmation",
          {
            body: {
              email: data.email,
              firstName: data.first_name,
              lastName: data.last_name,
              waitlistPosition: insertedData.waitlist_position,
              referralCode: insertedData.referral_code,
              referralLink,
              zipCode: data.zip_code,
              experienceLevel: data.experience_level,
              vehicleType: data.vehicle_type,
              featuresOfInterest: data.features_of_interest,
              referredByCode: data.referred_by_code,
              ageRange: data.age_range,
            },
          }
        );

        if (emailError) {
          console.error("Email send error:", emailError);
          // Don't fail the signup if email fails
        }
      } catch (e) {
        console.error("Email function invoke error:", e);
        // Don't fail the signup if email fails
      }

      return {
        waitlist_position: insertedData.waitlist_position,
        referral_code: insertedData.referral_code,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waitlist-count"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Only show count if >= 25
  const displayCount = waitlistCount && waitlistCount >= 25 ? waitlistCount : null;

  return {
    waitlistCount: displayCount,
    submitToWaitlist: submitMutation.mutateAsync,
    isSubmitting: submitMutation.isPending,
    submitError: submitMutation.error,
  };
};
